import { ApiResponse, Value } from 'app/types-shared';
import { endpoints } from './endpoints';
import { RemoteAdminData } from 'app/infrastructure/remote-data';
import {
  CategoryOptionsWire, CodeListWire, CodetableMetadataWire, LoadCategoryOptionsParams, LoadCodeItemsParams
} from 'app/area/import-mappings/types';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';

export const api = {
  addExternalValue: async (importSetId: number, optionsCollectionId: number, codeId: number, value: string) => {
    const response = await new RemoteAdminData(endpoints.saveExternalValue(importSetId))
      .resource<any>()
      .filter({ importSetId, optionsCollectionId, codeId })
      .create(value, false);

    return await response.json();
  },

  updateExternalValue: (
    importSetId: number, optionsCollectionId: number, codeId: number, importNumber: number, value: string
  ) => {
    return new RemoteAdminData(endpoints.saveExternalValue(importSetId))
      .resource<ApiResponse<any>>()
      .filter({ importSetId, optionsCollectionId, codeId, importNumber })
      .save(value);
  },

  deleteExternalValue: (
    importSetId: number, optionsCollectionId: number, codeId: number, importNumber: number
  ) => {
    return new RemoteAdminData(endpoints.saveExternalValue(importSetId))
      .resource<void>()
      .filter({ importSetId, optionsCollectionId, codeId, importNumber })
      .httpDelete({});
  },

  loadCodeItems: async (loadParams: LoadCodeItemsParams): Promise<CodeListWire> => {

    const { importSetId, id, parentId, siblingId, categoryId, displayNotSelected, startIndex, maxResults,
      limitFieldsTo, includeInactiveCodes, ignoreCategoryFilters, includeNotSelected, model } = loadParams;

    const response = await new RemoteAdminData(endpoints.optionsCollection(importSetId, id)).resource<Response>()
      .filter({
        ...(startIndex !== undefined && { startIndex }),
        ...(maxResults !== undefined && { maxResults }),
        ...(parentId !== undefined && { parentId }),
        ...(siblingId !== undefined && { siblingId }),
        ...(displayNotSelected !== undefined && { displayNotSelected }),
        ...(includeNotSelected !== undefined && { includeNotSelected })
      })
      .create({
        ...(includeInactiveCodes && !model && { includeInactiveCodes }),
        ...(limitFieldsTo && { limitFieldsTo }),
        ...(ignoreCategoryFilters && { ignoreCategoryFilters }),
        ...(categoryId !== undefined && {
          category: {
            selectedIds: [
              {
                v: categoryId
              }
            ]
          }
        }),
        ...model
      });

    const json = await response.json();

    return json.data;
  },

  loadMetadata: async (id: number): Promise<CodetableMetadataWire> => {
    const request = await new RemoteAdminData(endpoints.metadata(id))
      .resource<ApiResponse<CodetableMetadataWire>>()
      .single();

    return request.data;
  },

  loadImportSetOptions: async (): Promise<Value[]> => {
    const request = await new RemoteAdminData(endpoints.importSetOptions)
      .resource<ApiResponse<Value[]>>()
      .all();

    return request.data;
  },

  loadCodetableOptions: async (): Promise<Value[]> => {
    const request = await new RemoteAdminData(endpoints.codetableOptions)
      .resource<ApiResponse<Value[]>>()
      .all();

    return request.data;
  },

  loadCategoryOptions: async (loadParams: LoadCategoryOptionsParams): Promise<CategoryOptionsWire> => {
    const {
      id, parentId, siblingId, categoryId, limitFieldsTo, code, query, includeDisabled,
      displayNotSelected, returnLeavesOnly
    } = loadParams;

    const response = await new RemoteAdminData(endpoints.categoryOptions(id)).resource<Response>()
      .filter({
        ...(parentId !== undefined && { parentId }),
        ...(siblingId !== undefined && { siblingId }),
        ...(displayNotSelected !== undefined && { displayNotSelected }),
        ...(returnLeavesOnly !== undefined && { returnLeavesOnly })
      })
      .create({
        includeInactiveCodes: {
          enabled: includeDisabled
        },
        ...(limitFieldsTo && { limitFieldsTo }),
        ...(code && { code }),
        ...(!!query && {
          query: {
            text: query
          }
        }),
        ...(categoryId !== undefined && {
          category: {
            selectedIds: [
              {
                v: categoryId
              }
            ]
          }
        })
      });

    const json = await response.json();

    return json.data;
  },

  loadPath: async (codetableId: number, codeId: number): Promise<CodetableWireFormat> => {

    const response = await new RemoteAdminData(endpoints.optionPath(codetableId, codeId))
      .resource<ApiResponse<CodetableWireFormat>>()
      .single();

    return response.data;
  }
};
